import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Typography,
  Button,
  TextField,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Container,
  Paper,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import { API } from '../../config';
import { getApiErrorMessage } from '../../utils/apiError';

const states = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

const truckBodyTypes = [
  'Box Truck',
  'Flatbed Truck',
  'Refrigerated Truck',
  'Dump Truck',
  'Tanker Truck',
  'Lowboy Trailer',
  'Dry Van',
  'Step Deck',
  'Car Carrier',
  'Curtain Side Truck',
  'Hazmat Tanker',
  'Hot Shot Truck',
  'Pickup Truck',
  'Cargo Van',
  'Sprinter Van',
  'Gooseneck Trailer',
  'Tilt Trailer',
  'Logging Truck',
  'Tent Truck',
  'Other',
];

const CourierSignUp = () => {
  const history = useHistory();

  const [contactInfo, setContactInfo] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    country: 'United States',
    zipCode: '',
  });

  const [driverInfo, setDriverInfo] = useState({
    hasValidLicense: false,
    hasIphone: false,
    hasAndroid: false,
    hasAutoInsurance: false,
    liftAndCarryCapacity: '',
    courierType: '',
    truckBodyType: '',
    carryingCapacity: '',
    bodyVolume: '',
    licenseType: '',
    licenseNumber: '',
    licenseIssuedState: '',
    licenseExpirationDate: '',
    companyName: '',
    businessContact: '',
    ein: '',
  });

  const [vehicleInfo, setVehicleInfo] = useState({
    make: '',
    model: '',
    color: '',
    year: '',
    registration: '',
    licensePlate: '',
    equipment: '',
  });

  const [errors, setErrors] = useState({});
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [confirmLegalIdentity, setConfirmLegalIdentity] = useState(false);

  const handleContactInfoChange = (field, value) => {
    setContactInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDriverInfoChange = (field, value) => {
    setDriverInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVehicleInfoChange = (field, value) => {
    setVehicleInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);

    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const formatDateOfBirth = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);

    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;

    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const convertDateToBackendFormat = (value) => {
    if (!value || !/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;

    const [month, day, year] = value.split('/');
    return `${year}-${month}-${day}`;
  };

  const getLegalNameError = (value, label) => {
    const name = String(value || '').trim().replace(/\s+/g, ' ');

    if (!name) {
      return `${label} is required.`;
    }

    if (name.length > 100) {
      return `${label} must be 100 characters or fewer.`;
    }

    if (!/^[\p{L}\p{M}][\p{L}\p{M}'’ -]*$/u.test(name)) {
      return `${label} may contain letters, spaces, apostrophes, or hyphens only.`;
    }

    return '';
  };

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  const isValidDateOfBirth = (dob) => {
    const parts = dob.split('/');
    if (parts.length !== 3) return false;

    const month = Number(parts[0]);
    const day = Number(parts[1]);
    const year = Number(parts[2]);

    if (!month || !day || !year) return false;
    if (year < 1900) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    const birthDate = new Date(year, month - 1, day);

    if (
      birthDate.getFullYear() !== year ||
      birthDate.getMonth() !== month - 1 ||
      birthDate.getDate() !== day
    ) {
      return false;
    }

    const today = new Date();
    if (birthDate > today) return false;

    const ageDate = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate()
    );

    if (birthDate > ageDate) return false;

    return true;
  };

  const handleCourierSignUpSubmit = async (e) => {
    e.preventDefault();

    if (!confirmLegalIdentity) {
      alert(
        'Please confirm that your signup information is accurate and matches your legal identification, driver license, carrier authority, or business records.'
      );
      return;
    }

    if (!acceptTerms) {
      alert(
        'Please agree to the Terms, Privacy Policy, and Carrier Agreement before creating your driver account.'
      );
      return;
    }

    const validationErrors = {};

    if (!contactInfo.username || !contactInfo.username.trim()) {
      validationErrors.username = 'Username is required';
    }

    const firstNameError = getLegalNameError(
      contactInfo.firstName,
      'Legal first name'
    );

    if (firstNameError) {
      validationErrors.firstName = firstNameError;
    }

    const lastNameError = getLegalNameError(
      contactInfo.lastName,
      'Legal last name'
    );

    if (lastNameError) {
      validationErrors.lastName = lastNameError;
    }

    if (!isValidEmail(contactInfo.email)) {
      validationErrors.email = 'Please enter a valid email address.';
    }

    if (!contactInfo.password || !contactInfo.password.trim()) {
      validationErrors.password = 'Password is required';
    }

    if (contactInfo.password.length < 8) {
      validationErrors.password = 'Password must be at least 8 characters';
    }

    if (contactInfo.password !== contactInfo.confirmPassword) {
      validationErrors.confirmPassword = 'Passwords do not match';
    }

    if (
      !contactInfo.phoneNumber ||
      contactInfo.phoneNumber.replace(/\D/g, '').length !== 10
    ) {
      validationErrors.phoneNumber = 'Phone number must be 10 digits';
    }

    if (!isValidDateOfBirth(contactInfo.dateOfBirth)) {
      validationErrors.dateOfBirth =
        'Please enter a valid date of birth in MM/DD/YYYY format. You must be at least 18 years old.';
    }

    if (!contactInfo.address || !contactInfo.address.trim()) {
      validationErrors.address = 'Street Address is required';
    }

    if (!contactInfo.city || !contactInfo.city.trim()) {
      validationErrors.city = 'City is required';
    }

    if (!contactInfo.state || !contactInfo.state.trim()) {
      validationErrors.state = 'State is required';
    }

    if (!/^\d{5}$/.test(contactInfo.zipCode)) {
      validationErrors.zipCode = 'Zip Code must be 5 digits';
    }

    if (!driverInfo.courierType) {
      validationErrors.courierType = 'Account Type is required';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const courierType = (driverInfo.courierType || 'individual').toLowerCase();

    const courierData = {
      ...contactInfo,
      ...driverInfo,
      ...vehicleInfo,

      username: contactInfo.username.trim(),
      firstName: contactInfo.firstName.trim().replace(/\s+/g, ' '),
      lastName: contactInfo.lastName.trim().replace(/\s+/g, ' '),
      email: contactInfo.email.trim().toLowerCase(),
      password: contactInfo.password.trim(),
      phoneNumber: contactInfo.phoneNumber.trim(),
      dateOfBirth: convertDateToBackendFormat(contactInfo.dateOfBirth),
      address: contactInfo.address.trim(),
      city: contactInfo.city.trim(),
      state: contactInfo.state,
      country: contactInfo.country.trim(),
      zipCode: contactInfo.zipCode.trim(),
      courierType,
      legalIdentityConfirmed: confirmLegalIdentity,

      // Backend compatibility aliases
      first_name: contactInfo.firstName.trim().replace(/\s+/g, ' '),
      last_name: contactInfo.lastName.trim().replace(/\s+/g, ' '),
      phone: contactInfo.phoneNumber.trim(),
      date_of_birth: convertDateToBackendFormat(contactInfo.dateOfBirth),
      zip_code: contactInfo.zipCode.trim(),

      has_valid_license: driverInfo.hasValidLicense,
      has_iphone: driverInfo.hasIphone,
      has_android: driverInfo.hasAndroid,
      has_auto_insurance: driverInfo.hasAutoInsurance,
      lift_and_carry_capacity: driverInfo.liftAndCarryCapacity,
      courier_type: courierType,
      truck_body_type: driverInfo.truckBodyType,
      carrying_capacity: driverInfo.carryingCapacity,
      body_volume: driverInfo.bodyVolume,
      license_type: driverInfo.licenseType,
      license_number: driverInfo.licenseNumber,
      license_issued_state: driverInfo.licenseIssuedState,
      license_expiration_date: driverInfo.licenseExpirationDate,

      business_name: driverInfo.companyName,
      business_contact: driverInfo.businessContact,
      ein: driverInfo.ein,

      license_plate: vehicleInfo.licensePlate,
    };

    delete courierData.confirmPassword;

    try {
      const response = await fetch(`${API}/api/auth/register-courier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(courierData),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getApiErrorMessage(
            data,
            'Unable to create driver / carrier account. Please review the required fields and try again.'
          )
        );
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('courier_token', data.token);
      localStorage.setItem('userType', 'courier');
      localStorage.setItem(
        'courierType',
        courierType === 'professional' ? 'Professional' : 'Individual'
      );
      localStorage.setItem('role', 'courier');

      alert('Driver / Carrier account created successfully. You are now signed in.');
      history.push('/courier/dashboard');
    } catch (error) {
      console.error('Registration failed:', error.message);
      alert('Registration failed: ' + error.message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        py: { xs: 4, md: 6 },
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          px: { xs: 2.25, sm: 3 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3.5, md: 5 },
            borderRadius: 4,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 3,
              maxWidth: 560,
              mx: 'auto',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.95rem', sm: '2.2rem', md: '2.35rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.035em',
                color: '#0f172a',
                mb: 1,
              }}
            >
              Driver / Carrier Sign Up
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.55,
                mb: 3,
                fontSize: { xs: '0.98rem', sm: '1rem' },
              }}
            >
              Create a driver or carrier account to access loads, negotiate rates, and manage assignments.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.25,
                maxWidth: 430,
                mx: 'auto',
                mb: 2,
              }}
            >
              <Button
                component={Link}
                to="/courier/sign-up"
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  minHeight: 44,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.88rem', sm: '0.86rem', md: '0.9rem' },
                  letterSpacing: '0.025em',
                }}
              >
                CREATE ACCOUNT
              </Button>

              <Button
                component={Link}
                to="/courier/sign-in"
                variant="outlined"
                color="primary"
                fullWidth
                sx={{
                  minHeight: 44,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.88rem', sm: '0.86rem', md: '0.9rem' },
                  letterSpacing: '0.025em',
                }}
              >
                SIGN IN
              </Button>
            </Box>

            <Button
              component={Link}
              to="/"
              variant="text"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 800,
                fontSize: '0.82rem',
                letterSpacing: '0.04em',
                mb: 2,
              }}
            >
              BACK TO HOME
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleCourierSignUpSubmit} noValidate>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 1,
              }}
            >
              Contact Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Username"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.username}
                  onChange={(e) =>
                    handleContactInfoChange('username', e.target.value)
                  }
                  error={Boolean(errors.username)}
                  helperText={errors.username || ''}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Legal First Name"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.firstName}
                  onChange={(e) =>
                    handleContactInfoChange('firstName', e.target.value)
                  }
                  error={Boolean(errors.firstName)}
                  helperText={
                    errors.firstName ||
                    'Enter your legal first name as shown on your government ID or driver license.'
                  }
                  autoComplete="given-name"
                  inputProps={{ maxLength: 100 }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Legal Last Name"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.lastName}
                  onChange={(e) =>
                    handleContactInfoChange('lastName', e.target.value)
                  }
                  error={Boolean(errors.lastName)}
                  helperText={
                    errors.lastName ||
                    'Enter your legal last name as shown on your government ID or driver license.'
                  }
                  autoComplete="family-name"
                  inputProps={{ maxLength: 100 }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.email}
                  onChange={(e) =>
                    handleContactInfoChange('email', e.target.value)
                  }
                  error={Boolean(errors.email)}
                  helperText={errors.email || ''}
                  autoComplete="email"
                  inputProps={{
                    inputMode: 'email',
                    autoCapitalize: 'none',
                    spellCheck: false,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.password}
                  onChange={(e) =>
                    handleContactInfoChange('password', e.target.value)
                  }
                  error={Boolean(errors.password)}
                  helperText={errors.password || 'Minimum 8 characters'}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.confirmPassword}
                  onChange={(e) =>
                    handleContactInfoChange('confirmPassword', e.target.value)
                  }
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword || ''}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  placeholder="(555) 123-4567"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.phoneNumber}
                  onChange={(e) =>
                    handleContactInfoChange(
                      'phoneNumber',
                      formatPhoneNumber(e.target.value)
                    )
                  }
                  error={Boolean(errors.phoneNumber)}
                  helperText={errors.phoneNumber || 'Format: (555) 123-4567'}
                  inputProps={{
                    inputMode: 'tel',
                    maxLength: 14,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Birth"
                  placeholder="MM/DD/YYYY"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.dateOfBirth}
                  onChange={(e) =>
                    handleContactInfoChange(
                      'dateOfBirth',
                      formatDateOfBirth(e.target.value)
                    )
                  }
                  error={Boolean(errors.dateOfBirth)}
                  helperText={errors.dateOfBirth || 'MM/DD/YYYY'}
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 10,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Street Address"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.address}
                  onChange={(e) =>
                    handleContactInfoChange('address', e.target.value)
                  }
                  error={Boolean(errors.address)}
                  helperText={errors.address || ''}
                  autoComplete="street-address"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="City"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.city}
                  onChange={(e) =>
                    handleContactInfoChange('city', e.target.value)
                  }
                  error={Boolean(errors.city)}
                  helperText={errors.city || ''}
                  autoComplete="address-level2"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="State"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.state}
                  onChange={(e) =>
                    handleContactInfoChange('state', e.target.value)
                  }
                  error={Boolean(errors.state)}
                  helperText={errors.state || ''}
                  autoComplete="address-level1"
                  required
                >
                  {states.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Zip Code"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.zipCode}
                  onChange={(e) =>
                    handleContactInfoChange(
                      'zipCode',
                      e.target.value.replace(/\D/g, '').slice(0, 5)
                    )
                  }
                  inputProps={{ maxLength: 5, inputMode: 'numeric' }}
                  error={Boolean(errors.zipCode)}
                  helperText={errors.zipCode || ''}
                  autoComplete="postal-code"
                  required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 1,
              }}
            >
              Driver / Carrier Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Account Type"
                  variant="outlined"
                  fullWidth
                  value={driverInfo.courierType}
                  onChange={(e) =>
                    handleDriverInfoChange('courierType', e.target.value)
                  }
                  error={Boolean(errors.courierType)}
                  helperText={errors.courierType || ''}
                  required
                >
                  <MenuItem value="individual">Individual Driver</MenuItem>
                  <MenuItem value="professional">
                    Company / Professional Carrier
                  </MenuItem>
                </TextField>
              </Grid>

              {driverInfo.courierType !== 'professional' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Manual Load Capacity (lbs)"
                    variant="outlined"
                    fullWidth
                    value={driverInfo.liftAndCarryCapacity}
                    onChange={(e) =>
                      handleDriverInfoChange(
                        'liftAndCarryCapacity',
                        e.target.value
                      )
                    }
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
              )}

              {driverInfo.courierType === 'professional' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Company Name"
                      variant="outlined"
                      fullWidth
                      value={driverInfo.companyName}
                      onChange={(e) =>
                        handleDriverInfoChange('companyName', e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Business Contact"
                      variant="outlined"
                      fullWidth
                      value={driverInfo.businessContact}
                      onChange={(e) =>
                        handleDriverInfoChange(
                          'businessContact',
                          e.target.value
                        )
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="EIN / Authority Number"
                      variant="outlined"
                      fullWidth
                      value={driverInfo.ein}
                      onChange={(e) =>
                        handleDriverInfoChange('ein', e.target.value)
                      }
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1,
                      alignItems: 'flex-start',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={driverInfo.hasValidLicense}
                          onChange={(e) =>
                            handleDriverInfoChange(
                              'hasValidLicense',
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Valid License / Authority"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={driverInfo.hasAutoInsurance}
                          onChange={(e) =>
                            handleDriverInfoChange(
                              'hasAutoInsurance',
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Insurance"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={driverInfo.hasIphone}
                          onChange={(e) =>
                            handleDriverInfoChange('hasIphone', e.target.checked)
                          }
                        />
                      }
                      label="Has iPhone"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={driverInfo.hasAndroid}
                          onChange={(e) =>
                            handleDriverInfoChange(
                              'hasAndroid',
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Has Android"
                    />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Truck / Vehicle Body Type"
                  variant="outlined"
                  fullWidth
                  value={driverInfo.truckBodyType}
                  onChange={(e) =>
                    handleDriverInfoChange('truckBodyType', e.target.value)
                  }
                >
                  {truckBodyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Carrying Capacity (lbs)"
                  variant="outlined"
                  fullWidth
                  value={driverInfo.carryingCapacity}
                  onChange={(e) =>
                    handleDriverInfoChange('carryingCapacity', e.target.value)
                  }
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Body Volume"
                  variant="outlined"
                  fullWidth
                  value={driverInfo.bodyVolume}
                  onChange={(e) =>
                    handleDriverInfoChange('bodyVolume', e.target.value)
                  }
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="License Type"
                  variant="outlined"
                  fullWidth
                  value={driverInfo.licenseType}
                  onChange={(e) =>
                    handleDriverInfoChange('licenseType', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="License / Authority Number"
                  variant="outlined"
                  fullWidth
                  value={driverInfo.licenseNumber}
                  onChange={(e) =>
                    handleDriverInfoChange('licenseNumber', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="License Issued State"
                  variant="outlined"
                  fullWidth
                  value={driverInfo.licenseIssuedState}
                  onChange={(e) =>
                    handleDriverInfoChange(
                      'licenseIssuedState',
                      e.target.value
                    )
                  }
                >
                  {states.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="License Expiration Date"
                  placeholder="MM/DD/YYYY"
                  variant="outlined"
                  fullWidth
                  value={driverInfo.licenseExpirationDate}
                  onChange={(e) =>
                    handleDriverInfoChange(
                      'licenseExpirationDate',
                      formatDateOfBirth(e.target.value)
                    )
                  }
                  helperText="MM/DD/YYYY"
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 10,
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 1,
              }}
            >
              Vehicle / Fleet Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vehicle Make"
                  variant="outlined"
                  fullWidth
                  value={vehicleInfo.make}
                  onChange={(e) =>
                    handleVehicleInfoChange('make', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vehicle Model"
                  variant="outlined"
                  fullWidth
                  value={vehicleInfo.model}
                  onChange={(e) =>
                    handleVehicleInfoChange('model', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vehicle Color"
                  variant="outlined"
                  fullWidth
                  value={vehicleInfo.color}
                  onChange={(e) =>
                    handleVehicleInfoChange('color', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vehicle Year"
                  variant="outlined"
                  fullWidth
                  value={vehicleInfo.year}
                  onChange={(e) =>
                    handleVehicleInfoChange(
                      'year',
                      e.target.value.replace(/\D/g, '').slice(0, 4)
                    )
                  }
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 4,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Registration Number"
                  variant="outlined"
                  fullWidth
                  value={vehicleInfo.registration}
                  onChange={(e) =>
                    handleVehicleInfoChange('registration', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="License Plate"
                  variant="outlined"
                  fullWidth
                  value={vehicleInfo.licensePlate}
                  onChange={(e) =>
                    handleVehicleInfoChange('licensePlate', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Equipment / Special Features"
                  variant="outlined"
                  fullWidth
                  value={vehicleInfo.equipment}
                  onChange={(e) =>
                    handleVehicleInfoChange('equipment', e.target.value)
                  }
                />
              </Grid>
            </Grid>

            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {Object.values(errors).map((msg, index) => (
                  <Typography variant="body2" key={index}>
                    {msg}
                  </Typography>
                ))}
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  mb: 2,
                }}
                control={
                  <Checkbox
                    checked={confirmLegalIdentity}
                    onChange={(e) =>
                      setConfirmLegalIdentity(e.target.checked)
                    }
                    color="primary"
                    required
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm that my legal name, date of birth, phone number,
                    address, license / authority information, insurance
                    information, and vehicle or carrier details are accurate and
                    match my government ID, driver license, carrier authority, or
                    business records.
                  </Typography>
                }
              />

              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mt: 1, mb: 2, textAlign: 'left' }}
              >
                HaggleFreight may use this information for account security,
                fraud prevention, carrier eligibility, load assignments,
                transportation coordination, payment disputes, and platform
                safety.
              </Typography>

              <FormControlLabel
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                }}
                control={
                  <Checkbox
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    color="primary"
                    required
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer">
                      Terms
                    </Link>
                    ,{' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </Link>
                    , and{' '}
                    <Link
                      to="/carrier-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Carrier Agreement
                    </Link>
                    .
                  </Typography>
                }
              />

              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mt: 1, textAlign: 'left' }}
              >
                By creating a driver account, you agree to HaggleFreight’s
                platform rules, payment policies, carrier responsibilities,
                safety requirements, and legal agreements.
              </Typography>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={!acceptTerms || !confirmLegalIdentity}
                sx={{
                  mt: 3,
                  minHeight: 46,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  letterSpacing: '0.025em',
                }}
              >
                CREATE DRIVER / CARRIER ACCOUNT
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default CourierSignUp;
